<script>
/*
  Vision Venture — Healthy Eyes Grid (VV Home "ค่าสายตาล่าสุด")
  - Reuses the same custom-object data source and field-resolution rules as
    src/views/page/eyeRecord/VvEyeRecord.vue (OculusTypeID 1 = left, 2 = right)
  - Shows only the single latest record per eye side; full history stays on
    /eye-record
  - Layout wrap อยู่ที่ VisionVentureHome.vue (section-level component)
*/

const normalizeFieldName = value =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const getFirstAnswerValue = field => {
  const answerDetail = Array.isArray(field?.answer_detail)
    ? field.answer_detail
    : [];
  const answer = answerDetail.find(
    item => item?.value_answer !== null && item?.value_answer !== undefined
  );

  return answer?.value_answer ?? "";
};

const ANSWER_PAGE_SIZE = 20;

export default {
  name: "VvHealthyEyesGrid",
  data() {
    return {
      isLoading: false,
      latestRight: null,
      latestLeft: null
    };
  },
  computed: {
    resolvedUserGuid() {
      return String(this.$store.state.shortProfile?.user_guid || "").trim();
    },
    sides() {
      return [
        { key: "right", label: this.$t("eye_record_right_eye") },
        { key: "left", label: this.$t("eye_record_left_eye") }
      ].map(side => ({
        ...side,
        record: side.key === "right" ? this.latestRight : this.latestLeft
      }));
    },
    metrics() {
      return [
        { key: "sph", label: this.$t("eye_record_sph_label") },
        { key: "cyl", label: this.$t("eye_record_cyl_label") },
        { key: "add", label: this.$t("eye_record_add_label") }
      ];
    },
    hasRecords() {
      return Boolean(this.latestRight || this.latestLeft);
    }
  },
  created() {
    this.loadLatestEyeRecords();
  },
  methods: {
    async loadLatestEyeRecords() {
      this.isLoading = true;

      try {
        const customObjectId = await this.getEyeRecordCustomObjectId();
        const answers = customObjectId
          ? await this.getEyeRecordAnswers(customObjectId)
          : [];
        const { right, left } = this.resolveLatestBySide(answers);
        this.latestRight = right;
        this.latestLeft = left;
      } finally {
        this.isLoading = false;
      }
    },
    async getEyeRecordCustomObjectId() {
      const endpoint = "/api/CustomObject/custom_object_head";
      const payload = { user_guid: this.resolvedUserGuid };

      try {
        const response = await this.$axios.post(endpoint, payload);
        return response?.detail?.[0]?.id || null;
      } catch (error) {
        this.$logger({
          response: error?.response || {
            config: error?.config || {
              url: endpoint,
              method: "POST",
              methods: "POST",
              data: JSON.stringify(payload)
            }
          },
          action: {
            id: 3,
            action: "VvHealthyEyesGrid getEyeRecordCustomObjectId API request failed"
          }
        });
        return null;
      }
    },
    async getEyeRecordAnswers(customObjectId) {
      const endpoint = "/api/CustomObject/answer_by_custom_object_id";
      const payload = {
        custom_object_id: customObjectId,
        search: "",
        user_guid: this.resolvedUserGuid,
        date_from: "",
        date_to: "",
        page: 1,
        page_size: ANSWER_PAGE_SIZE
      };

      try {
        const response = await this.$axios.post(endpoint, payload);
        return Array.isArray(response?.detail) ? response.detail : [];
      } catch (error) {
        this.$logger({
          response: error?.response || {
            config: error?.config || {
              url: endpoint,
              method: "POST",
              methods: "POST",
              data: JSON.stringify(payload)
            }
          },
          action: {
            id: 3,
            action: "VvHealthyEyesGrid getEyeRecordAnswers API request failed"
          }
        });
        return [];
      }
    },
    resolveLatestBySide(answers) {
      const latest = { right: null, left: null };

      answers.forEach((answer, index) => {
        const fields = this.createAnswerFieldMap(answer?.datas);
        const side = this.resolveEyeSide(fields);

        if (!side) return;

        const rawDate = this.resolveAnswerDate(answer, fields);
        const sortValue = this.getRecordSortValue(rawDate, index);
        const record = {
          date: this.formatRecordDate(rawDate),
          sortValue,
          sourceIndex: index,
          values: {
            sph: this.toNumericValue(
              this.readFieldValue(fields, ["SPH", "Sphere"])
            ),
            cyl: this.toNumericValue(
              this.readFieldValue(fields, ["CYL", "Cylinder"])
            ),
            add: this.toNumericValue(this.readFieldValue(fields, ["AXIS"]))
          }
        };

        const current = latest[side];
        const isNewer =
          !current ||
          sortValue > current.sortValue ||
          (sortValue === current.sortValue &&
            index > current.sourceIndex);

        if (isNewer) latest[side] = record;
      });

      return latest;
    },
    createAnswerFieldMap(datas) {
      return (Array.isArray(datas) ? datas : []).reduce((fields, field) => {
        const fieldName = normalizeFieldName(field?.field_name || field?.name);

        if (fieldName) fields[fieldName] = getFirstAnswerValue(field);
        return fields;
      }, {});
    },
    readFieldValue(fields, aliases) {
      for (const alias of aliases) {
        const value = fields[normalizeFieldName(alias)];
        if (value !== "" && value !== null && value !== undefined) return value;
      }

      return "";
    },
    resolveEyeSide(fields) {
      const sideValue = normalizeFieldName(
        this.readFieldValue(fields, [
          "OculusTypeID",
          "OculusType",
          "EyeSide",
          "Side"
        ])
      );

      if (
        ["2", "r", "right", "od"].includes(sideValue) ||
        sideValue.includes("right") ||
        sideValue.includes("dexter")
      ) {
        return "right";
      }
      if (
        ["1", "l", "left", "os"].includes(sideValue) ||
        sideValue.includes("left") ||
        sideValue.includes("sinister")
      ) {
        return "left";
      }

      return null;
    },
    resolveAnswerDate(answer, fields) {
      const fieldDate = this.readFieldValue(fields, [
        "ExaminationDate",
        "ExamDate",
        "RecordDate",
        "Date"
      ]);

      return (
        answer?.created_time ||
        fieldDate ||
        answer?.answer_date ||
        answer?.answer_datetime ||
        answer?.created_at ||
        answer?.create_at ||
        answer?.created_date ||
        answer?.date_created ||
        answer?.create_date ||
        answer?.updated_at ||
        answer?.modified_at ||
        ""
      );
    },
    formatRecordDate(value) {
      const date = this.parseRecordDate(value);
      return date.isValid() ? date.format("DD/MM/YY") : "-";
    },
    getRecordSortValue(value, fallbackIndex) {
      const date = this.parseRecordDate(value);
      return date.isValid() ? date.valueOf() : fallbackIndex;
    },
    parseRecordDate(value) {
      return this.$moment(value, [
        "MM/DD/YYYY HH:mm:ss",
        "YYYY-MM-DDTHH:mm:ss.SSSZ",
        "YYYY-MM-DDTHH:mm:ssZ",
        "YYYY-MM-DD HH:mm:ss",
        "YYYY-MM-DD"
      ]);
    },
    toNumericValue(value) {
      if (value === "" || value === null || value === undefined) return null;

      const numberValue = Number(String(value).replace(",", "."));
      return Number.isFinite(numberValue) ? numberValue : null;
    },
    formatMetricValue(record, metricKey) {
      const value = record?.values?.[metricKey];
      if (!Number.isFinite(value)) return "-";

      return `${value > 0 ? "+" : ""}${value.toFixed(2)}`;
    },
    goEyeRecordHistory() {
      this.$router.push("/eye-record");
    }
  }
};
</script>

<template>
  <section class="vv-healthy-eyes" data-test="vv-home-eye-record-section">
    <header class="vv-healthy-eyes__header">
      <h2>{{ $t("eye_record_home_title") }}</h2>
      <button
        type="button"
        class="vv-healthy-eyes__history-link"
        data-test="vv-home-eye-record-history-btn"
        @click="goEyeRecordHistory"
      >
        {{ $t("eye_record_home_history_link") }}
        <font-awesome-icon icon="chevron-right" />
      </button>
    </header>

    <div
      v-if="isLoading"
      class="vv-healthy-eyes__state"
      data-test="vv-home-eye-record-loading"
    >
      {{ $t("loading") }}
    </div>

    <div
      v-else-if="!hasRecords"
      class="vv-healthy-eyes__state"
      data-test="vv-home-eye-record-empty"
    >
      {{ $t("eye_record_home_empty") }}
    </div>

    <div v-else class="vv-healthy-eyes__rows">
      <div
        v-for="side in sides"
        :key="side.key"
        class="vv-healthy-eyes__row"
        :data-test="`vv-home-eye-record-${side.key}-row`"
      >
        <div class="vv-healthy-eyes__side">
          <span
            class="vv-healthy-eyes__side-icon"
            :class="`vv-healthy-eyes__side-icon--${side.key}`"
            aria-hidden="true"
          >
            <font-awesome-icon :icon="['fas', 'eye']" />
          </span>
          <strong>{{ side.label }}</strong>
        </div>

        <span class="vv-healthy-eyes__divider" aria-hidden="true"></span>

        <div class="vv-healthy-eyes__metrics">
          <div
            v-for="metric in metrics"
            :key="metric.key"
            class="vv-healthy-eyes__metric"
            :data-test="`vv-home-eye-record-${side.key}-${metric.key}`"
          >
            <span class="vv-healthy-eyes__metric-label">{{ metric.label }}</span>
            <span class="vv-healthy-eyes__metric-value">
              {{ formatMetricValue(side.record, metric.key) }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.vv-healthy-eyes {
  --vv-healthy-eyes-right-color: var(--crm-success-color);
  --vv-healthy-eyes-left-color: var(--button-color);
}

.vv-healthy-eyes__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 0 2px;
}

.vv-healthy-eyes__header h2 {
  margin: 0;
  color: var(--text-color);
  font-size: var(--font-title-sm);
  font-weight: 900;
}

.vv-healthy-eyes__history-link {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 4px;
  border: 0;
  background: transparent;
  color: var(--crm-link-color);
  font-size: var(--font-body);
  font-weight: 500;
  opacity: 0.8;
}

.vv-healthy-eyes__state {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 72px;
  margin-top: 10px;
  padding: 18px 12px;
  border-radius: 20px;
  background: var(--crm-card-glass-background);
  box-shadow: var(--crm-vv-reward-card-shadow);
  backdrop-filter: var(--crm-card-glass-backdrop-filter);
  -webkit-backdrop-filter: var(--crm-card-glass-backdrop-filter);
  color: var(--text-color);
  font-size: var(--font-body);
  text-align: center;
}

.vv-healthy-eyes__rows {
  margin-top: 10px;
  overflow: hidden;
  border-radius: 20px;
  background: var(--crm-card-glass-background);
  box-shadow: var(--crm-vv-reward-card-shadow);
  backdrop-filter: var(--crm-card-glass-backdrop-filter);
  -webkit-backdrop-filter: var(--crm-card-glass-backdrop-filter);
}

.vv-healthy-eyes__row {
  display: grid;
  grid-template-columns: auto auto 1fr;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
}

.vv-healthy-eyes__row + .vv-healthy-eyes__row {
  border-top: 1px solid var(--secondary-color-light);
}

.vv-healthy-eyes__side {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  min-width: 66px;
  color: var(--text-color);
  font-size: var(--font-body);
  font-weight: 700;
  text-align: center;
}

.vv-healthy-eyes__side-icon {
  position: relative;
  display: inline-flex;
  overflow: hidden;
  width: 44px;
  height: 44px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  color: var(--vv-healthy-eyes-right-color);
  font-size: var(--font-title-sm);
}

.vv-healthy-eyes__side-icon::before {
  position: absolute;
  inset: 0;
  background: currentColor;
  content: "";
  opacity: 0.14;
}

.vv-healthy-eyes__side-icon > * {
  position: relative;
  z-index: 1;
}

.vv-healthy-eyes__side-icon--left {
  color: var(--vv-healthy-eyes-left-color);
}

.vv-healthy-eyes__divider {
  align-self: stretch;
  width: 1px;
  background: var(--secondary-color-light);
}

.vv-healthy-eyes__metrics {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}

.vv-healthy-eyes__metric {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  text-align: center;
}

.vv-healthy-eyes__metric-label {
  color: var(--text-color);
  font-size: var(--font-body-sm);
  opacity: 0.5;
}

.vv-healthy-eyes__metric-value {
  color: var(--text-color);
  font-size: var(--font-body-lg);
  font-weight: 700;
}
</style>
